
import { Component, Vue, Inject, Watch } from 'vue-property-decorator';
import { Auth } from '@aws-amplify/auth';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Container } from 'inversify';
import { AlarmModel, AlarmSeverity, Convert } from '@/flightpath-components/models/AlarmModel';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { ConvertMachineKey, MachineKey } from '@/flightpath-components/models/MachineKey';
import { getModule } from 'vuex-module-decorators';
import AlarmStore from '@/store/modules/alarms';

// Define the component in class-style
@Component({ name: 'Alarm' })
export default class Alarm extends Vue {
  valid = false;
  signedIn = false;
  tabs = null;
  loading = false;
  dialog = false;
  selectedMachineByName: string | null = null;
  selected: Array<AlarmModel> = [];
  headers = [
    {
      text: this.$t('views.alarms.headers.type'),
      value: 'Description'
    },
    { text: this.$t('views.alarms.headers.active'), value: 'Active' },
    { text: this.$t('views.alarms.headers.machine_name'), value: 'MachineName' },
    { text: this.$t('views.alarms.headers.date'), value: 'AlarmTime' },
    { text: '', value: 'actions', sortable: false }
  ];
  tabCollection = [
    { name: 'errors', filter: 'errorItems', total: 'errorItemsTotal' },
    { name: 'warnings', filter: 'warningItems', total: 'warningItemsTotal' },
    { name: 'notifications', filter: 'notificationItems', total: 'notificationItemsTotal' }
  ];
  alarmStore = getModule(AlarmStore, this.$store);
  editedIndex = -1;
  editedItem: AlarmModel = {
    Active: false,
    AlarmTime: new Date(),
    UserName: '',
    Package: '',
    NodeName: '',
    AlarmId: 0,
    Severity: AlarmSeverity.Notification,
    Description: '',
    Overridden: false,
    Action: '',
    Parameters: '',
    AllowTransportation: false,
    Frequency: 0,
    MachineId: '',
    Acknowledged: false,
    identifier: '0',
    MachineName: '',
    CompositeKey: '',
    AlarmReference: ''
  };
  defaultItem: AlarmModel = {
    Active: false,
    AlarmTime: new Date(),
    UserName: '',
    Package: '',
    NodeName: '',
    AlarmId: 0,
    Severity: AlarmSeverity.Notification,
    Description: '',
    Overridden: false,
    Action: '',
    Parameters: '',
    AllowTransportation: false,
    Frequency: 0,
    MachineId: '',
    Acknowledged: false,
    identifier: '0',
    MachineName: '',
    CompositeKey: '',
    AlarmReference: ''
  };

  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  // IoC container provided from App.ts is injected here
  @Inject('container')
  private container!: Container;

  @Watch('dialog')
  onDialogChange(val: boolean) {
    return val || this.close();
  }

  get machineId() {
    return this.$route.params.machineId;
  }
  itemRowBackground(item: AlarmModel) {
    return item.Acknowledged == true ? 'row-ack' : 'row-no-ack';
  }

  evalInContext(val: string) {
    try {
      return eval('this.' + val);
    } catch (error) {
      try {
        return eval(val);
      } catch (err) {
        console.warn('Error evaluating context: ' + val, err);
        return null;
      }
    }
  }
  activeColor(item: boolean) {
    return item ? '#49cc56' : 'grey';
  }
  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
  }

  viewItem(item: AlarmModel) {
    this.editedIndex = this.alarmStore.alarms.indexOf(item);
    this.editedItem = Object.assign({}, item);
    this.dialog = true;
  }
  close() {
    this.dialog = false;
    this.$nextTick(() => {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
    });
  }

  async archive(item: AlarmModel) {
    this.selected = [item];
    this.selected.forEach(async alarm => {
      if (this.flightPathService == undefined) return;
      const user = await Auth.currentAuthenticatedUser();
      await this.flightPathService.acknowledgeAlarm([alarm], user.attributes.sub);
    });
    this.getAlarms();
    this.selected = [];
    this.close();
  }
  async archiveSelected() {
    if (this.flightPathService == undefined) return;
    const user = await Auth.currentAuthenticatedUser();
    await this.flightPathService.acknowledgeAlarm(this.selected, user.attributes.sub);
    this.getAlarms();
    this.selected = [];
    this.close();
  }

  get alarmList() {
    if (this.machineFilterApplied) {
      const filteredMachine = this.machineId;
      return this.alarmStore.alarms.filter(value => filteredMachine == null || filteredMachine == '' || value.MachineId == filteredMachine);
    }
    return this.alarmStore.alarms.filter(value => this.selectedMachineByName == null || this.selectedMachineByName == value.MachineName);
  }

  get machineFilterApplied() {
    return this.$route.params?.machineId?.length > 0;
  }
  async getAlarms() {
    if (this.flightPathService == undefined) return;
    this.loading = true;
    const user = await Auth.currentAuthenticatedUser();
    const machineKeys = await this.getCurrentMachineKeys();

    const machineIds: string[] = [];
    machineKeys.forEach(val => {
      machineIds.push(val.machineId);
    });

    if (machineIds.length > 0) {
      const response = await this.flightPathService.getAlarmTable(user.attributes.sub, JSON.stringify(machineIds));
      console.log(response);

      const alarmModel = Convert.stringToAlarmModelList(JSON.stringify(response.data));

      alarmModel.forEach((alarm: AlarmModel) => {
        alarm.AlarmTime = new Date(alarm.AlarmTime);
        const index = machineKeys.findIndex(key => key.machineId === alarm.MachineId);
        if (index == -1) {
          alarm.MachineName = 'None';
        } else {
          alarm.MachineName = machineKeys[index].MachineName ?? 'None';
        }
      });
      if (alarmModel != null) {
        this.alarmStore.changeAlarms(alarmModel);
      }
    }
    this.loading = false;
  }

  async getCurrentMachineKeys(): Promise<MachineKey[]> {
    if (this.flightPathService == undefined) return [];

    const machineResponse = await this.flightPathService.getAvailableMachines();
    const currentMachines = ConvertMachineKey.toMachineKey(JSON.stringify(machineResponse.data));

    return currentMachines;
  }

  get selectedMachineName(): string | null {
    return this.selectedMachineByName;
  }
  set selectedMachineName(value: string | null) {
    this.selectedMachineByName = value;
  }

  get machineNames() {
    return this.alarmStore.alarms.filter((thing, i, arr) => arr.findIndex(t => t.MachineId === thing.MachineId) === i).map(a => a.MachineName);
  }
  get errorItems() {
    return this.alarmList.filter(item => {
      return item.Severity == AlarmSeverity.Error;
    });
  }
  get errorItemsTotal() {
    return this.alarmList.filter(item => {
      return item.Severity == AlarmSeverity.Error && item.Active;
    }).length;
  }
  get warningItems() {
    return this.alarmList.filter(item => {
      return item.Severity == AlarmSeverity.Warning;
    });
  }
  get warningItemsTotal() {
    return this.alarmList.filter(item => {
      return item.Severity == AlarmSeverity.Warning && item.Active;
    }).length;
  }
  get notificationItems() {
    return this.alarmList.filter(item => {
      return item.Severity == AlarmSeverity.Notification;
    });
  }
  get notificationItemsTotal() {
    return this.alarmList.filter(item => {
      return item.Severity == AlarmSeverity.Notification && item.Active;
    }).length;
  }

  getTranslatedAlarmDescription(alarm: AlarmModel) {
    return this.$t(`alarms.description.` + this.getAlarmKey(alarm));
  }

  getTranslatedAlarmAction(alarm: AlarmModel) {
    return this.$t(`alarms.action.` + this.getAlarmKey(alarm));
  }

  getAlarmKey(alarm: AlarmModel) {
    return alarm.Package + '_' + alarm.NodeName + '_' + alarm.AlarmId;
  }

  beforeCreate() {
    Auth.currentAuthenticatedUser()
      .then(user => {
        if (user != undefined) {
          this.signedIn = true;
        }
      })
      .catch(() => console.log('not signed in...'));
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    this.getAlarms();
  }
}
